import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { STATES_TYPE } from '@constants/configs/states';
import { useDebounce } from '@containers/common/useDebounce';
import Dropdown from '@lib/inputs/Dropdown';
import Input from '@lib/inputs/Input';

import { GridStyledWrapper } from '../voterRegistration.styled';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import useVoterRegistration from '../useVoterRegistration';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';
import { RegistrationReason } from '../constants/voterRegistrationConstants';

function VotingChangedInformationSection() {
    const intl = useIntl();
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<IVoterRegistrationFormValues>();
    const { VOTER_REGISTRATION_CHOICES, getCounties, DROPDOWN_DEFAULT_OPTION } = useVoterRegistration();
    const [countyOptions, setCountyOptions] = React.useState([DROPDOWN_DEFAULT_OPTION]);

    const state = watch('votingInformationChanged.state');
    const zipError = errors?.votingInformationChanged?.zip?.message;
    const stateError = errors?.votingInformationChanged?.state?.message;
    const zipCode = watch('votingInformationChanged.zip');
    const county = watch('votingInformationChanged.county');
    const changeOfReason = watch('registrationReason') as RegistrationReason[];
    const isNewRegistrationSelected = changeOfReason?.includes(RegistrationReason.NEW_REGISTRATION) || false;
    const isChangeOfNameSelected = changeOfReason?.includes(RegistrationReason.CHANGE_OF_NAME) || false;
    const isChangeOfAddressSelected = changeOfReason?.includes(RegistrationReason.CHANGE_OF_ADDRESS) || false;
    const debounceZipCode: string = useDebounce(zipCode, 500);

    const handleFieldChange = (name: string, value: any) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    useEffect(() => {
        if (state && debounceZipCode && debounceZipCode.length === 5 && !zipError && !stateError) {
            const fetchCounties = async () => {
                const counties: Record<string, string>[] = await getCounties(state as STATES_TYPE, debounceZipCode);
                if (counties) {
                    const formattedCounties = counties.map(obj => {
                        const [_value, text] = Object.entries(obj)[0];
                        return { text, value: text };
                    });

                    formattedCounties.unshift(DROPDOWN_DEFAULT_OPTION);

                    setCountyOptions(formattedCounties);
                    setValue('votingInformationChanged.county', DROPDOWN_DEFAULT_OPTION.value);
                }
            };
            fetchCounties();
        } else {
            setCountyOptions([DROPDOWN_DEFAULT_OPTION]);
            setValue('votingInformationChanged.county', DROPDOWN_DEFAULT_OPTION.value);
        }
    }, [state, debounceZipCode, zipError, stateError, intl]);

    return (
        <div id="votingInformationChanged-section">
            <FormattedMessage id="voterRegistration.form.votingChangedInformation.header" tagName="h2" />
            <AlertMessage
                type={'info'}
                content={
                    <>
                        <FormattedMessage
                            id="voterRegistration.form.votingChangedInformation.alert.1"
                            values={{ strong: (chunks: any) => <strong>{chunks}</strong> }}
                        />
                        <br />
                        <FormattedMessage id="voterRegistration.form.votingChangedInformation.alert.2" />
                    </>
                }
                showIcon={true}
                icon={'info-circle'}
            />
            <GridStyledWrapper>
                <div>
                    <Input
                        label={{
                            text: intl.formatMessage({
                                id: 'voterRegistration.form.votingChangedInformation.registrationNumber',
                            }),
                        }}
                        inputData={{
                            id: 'votingInformationChanged.registrationNumber',
                            name: 'votingInformationChanged.registrationNumber',
                            value: watch('votingInformationChanged.registrationNumber'),
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val =>
                                handleFieldChange('votingInformationChanged.registrationNumber', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.registrationNumber"
                        hasError={!!errors?.votingInformationChanged?.registrationNumber}
                        messageId={errors?.votingInformationChanged?.registrationNumber?.message}
                    />
                </div>
                <div className="empty" />
                <div>
                    <Input
                        label={{
                            text: intl.formatMessage({
                                id: 'voterRegistration.form.votingChangedInformation.previousFirstName',
                            }),
                        }}
                        inputData={{
                            id: 'votingInformationChanged.previousFirstName',
                            name: 'votingInformationChanged.previousFirstName',
                            value: watch('votingInformationChanged.previousFirstName'),
                            isInputRequired: isChangeOfNameSelected,
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val =>
                                handleFieldChange('votingInformationChanged.previousFirstName', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.previousFirstName"
                        hasError={!!errors?.votingInformationChanged?.previousFirstName}
                        messageId={errors?.votingInformationChanged?.previousFirstName?.message}
                    />
                </div>
                <div>
                    <Input
                        label={{
                            text: intl.formatMessage({
                                id: 'voterRegistration.form.votingChangedInformation.previousLastName',
                            }),
                        }}
                        inputData={{
                            id: 'votingInformationChanged.previousLastName',
                            name: 'votingInformationChanged.previousLastName',
                            value: watch('votingInformationChanged.previousLastName'),
                            isInputRequired: isChangeOfNameSelected,
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val =>
                                handleFieldChange('votingInformationChanged.previousLastName', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.previousLastName"
                        hasError={!!errors?.votingInformationChanged?.previousLastName}
                        messageId={errors?.votingInformationChanged?.previousLastName?.message}
                    />
                </div>
                <div className="fullWidth">
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.votingChangedInformation.address' }),
                        }}
                        inputData={{
                            id: 'votingInformationChanged.address',
                            name: 'votingInformationChanged.address',
                            value: watch('votingInformationChanged.address'),
                            isInputRequired: isChangeOfAddressSelected,
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('votingInformationChanged.address', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.address"
                        hasError={!!errors?.votingInformationChanged?.address}
                        messageId={errors?.votingInformationChanged?.address?.message}
                    />
                </div>
                <div>
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.votingChangedInformation.city' }),
                        }}
                        inputData={{
                            id: 'votingInformationChanged.city',
                            name: 'votingInformationChanged.city',
                            value: watch('votingInformationChanged.city'),
                            isInputRequired: isChangeOfAddressSelected,
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('votingInformationChanged.city', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.city"
                        hasError={!!errors?.votingInformationChanged?.city}
                        messageId={errors?.votingInformationChanged?.city?.message}
                    />
                </div>
                <div>
                    <Dropdown
                        label={{
                            text: <FormattedMessage id="voterRegistration.form.votingChangedInformation.state" />,
                        }}
                        inputData={{
                            id: 'votingInformationChanged.state',
                            name: 'votingInformationChanged.state',
                            value: state,
                            options: VOTER_REGISTRATION_CHOICES.US_STATES,
                            isInputRequired: isChangeOfAddressSelected,
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('votingInformationChanged.state', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.state"
                        hasError={!!errors?.votingInformationChanged?.state}
                        messageId={errors?.votingInformationChanged?.state?.message}
                    />
                </div>
                <div>
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.votingChangedInformation.zip' }),
                        }}
                        inputData={{
                            id: 'votingInformationChanged.zip',
                            name: 'votingInformationChanged.zip',
                            value: zipCode,
                            isInputRequired: isChangeOfAddressSelected,
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('votingInformationChanged.zip', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.zip"
                        hasError={!!errors?.votingInformationChanged?.zip}
                        messageId={errors?.votingInformationChanged?.zip?.message}
                    />
                </div>
                <div>
                    <Dropdown
                        label={{
                            text: <FormattedMessage id="voterRegistration.form.votingChangedInformation.county" />,
                        }}
                        inputData={{
                            id: 'votingInformationChanged.county',
                            name: 'votingInformationChanged.county',
                            value: county,
                            options: countyOptions,
                            isInputRequired: isChangeOfAddressSelected,
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('votingInformationChanged.county', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.county"
                        hasError={!!errors?.votingInformationChanged?.county}
                        messageId={errors?.votingInformationChanged?.county?.message}
                    />
                </div>
                <div>
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.votingChangedInformation.year' }),
                        }}
                        inputData={{
                            id: 'votingInformationChanged.year',
                            name: 'votingInformationChanged.year',
                            value: watch('votingInformationChanged.year'),
                            disabled: isNewRegistrationSelected,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('votingInformationChanged.year', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="votingInformationChanged.year"
                        hasError={!!errors?.votingInformationChanged?.year}
                        messageId={errors?.votingInformationChanged?.year?.message}
                    />
                </div>
            </GridStyledWrapper>
        </div>
    );
}

export default VotingChangedInformationSection;
